import React, { useEffect, useState } from "react";
import { Users, Globe2, Cake, User2, Info } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", CA: "Canada", BR: "Brazil",
  FR: "France", DE: "Germany", NG: "Nigeria", ZA: "South Africa",
  KE: "Kenya", IN: "India", MX: "Mexico", JP: "Japan", AU: "Australia",
  ES: "Spain", IT: "Italy", PH: "Philippines", ID: "Indonesia", PK: "Pakistan",
  BD: "Bangladesh", "??": "Unknown",
};
const AGE_ORDER = ["13-17", "18-24", "25-34", "35-54", "55+", "unknown"];
const GENDER_ORDER = ["female", "male", "nonbinary", "other", "unknown"];

function Bars({ rows, accent }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="space-y-2">
      {rows.map(r => {
        const pct = Math.round((r.count / total) * 100);
        return (
          <div key={r.key}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/75">{r.label}</span>
              <span className="text-white/50 fl-mono">{r.count} · {pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className={`h-full rounded-full ${accent}`}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Live listener-demographics panel for a station owner. Pulls /analytics
 *  every 30s while mounted. All rows show actual heartbeat data — listeners
 *  who registered with optional age/gender feed those splits; everyone else
 *  appears under 'unknown'. */
export default function DemographicsPanel({ stationId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!stationId) return;
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/stations/${stationId}/analytics`, { headers: authHeaders() });
        if (alive) { setData(data); setErr(""); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Couldn't load analytics");
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [stationId]);

  if (!stationId) return null;
  if (err) return <div className="fl-card p-5 text-rose-400 text-sm" data-testid="demographics-error">{err}</div>;
  if (!data) return <div className="fl-card p-5 text-white/40 text-sm">Loading demographics…</div>;

  const countries = Object.entries(data.listeners_by_country || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([k, v]) => ({ key: k, label: COUNTRY_NAMES[k] || k, count: v }));
  const ages = AGE_ORDER
    .filter(k => (data.listeners_by_age || {})[k])
    .map(k => ({ key: k, label: k === "unknown" ? "Not shared" : k, count: data.listeners_by_age[k] }));
  const genders = GENDER_ORDER
    .filter(k => (data.listeners_by_gender || {})[k])
    .map(k => ({
      key: k,
      label: k === "unknown" ? "Not shared" : k.charAt(0).toUpperCase() + k.slice(1),
      count: data.listeners_by_gender[k],
    }));

  return (
    <section className="space-y-4" data-testid="demographics-panel">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h3 className="fl-display text-xl font-bold">Listener demographics</h3>
          <div className="text-xs text-white/40 mt-1">
            Live snapshot of the last 90 seconds · {data.current_listeners} total · {data.known_demographics_pct}% shared their age
          </div>
        </div>
      </div>

      {data.current_listeners === 0 ? (
        <div className="fl-card p-6 flex items-start gap-3" data-testid="demographics-empty">
          <Info className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-white font-semibold">No live listeners right now</div>
            <div className="text-white/55 text-sm mt-1 leading-relaxed">
              Demographics appear here as soon as people tune in. Share your station
              on social media to start collecting real audience data.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="fl-card p-5" data-testid="demographics-locations">
            <div className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
              <Globe2 className="w-4 h-4 text-emerald-400" /> Locations
            </div>
            {countries.length ? (
              <Bars rows={countries} accent="bg-emerald-500" />
            ) : (
              <div className="text-white/40 text-xs">No country data yet.</div>
            )}
          </div>

          <div className="fl-card p-5" data-testid="demographics-age">
            <div className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
              <Cake className="w-4 h-4 text-fuchsia-400" /> Age
            </div>
            {ages.length ? (
              <Bars rows={ages} accent="bg-fuchsia-500" />
            ) : (
              <div className="text-white/40 text-xs">Not shared yet.</div>
            )}
          </div>

          <div className="fl-card p-5" data-testid="demographics-gender">
            <div className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
              <User2 className="w-4 h-4 text-emerald-400" /> Gender
            </div>
            {genders.length ? (
              <Bars rows={genders} accent="bg-emerald-500" />
            ) : (
              <div className="text-white/40 text-xs">Not shared yet.</div>
            )}
          </div>
        </div>
      )}

      <div className="text-xs text-white/35 flex items-start gap-2">
        <Users className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Age and gender come from listeners who chose to share that on their Fliboe profile.
          Anonymous and silent listeners count toward the total but appear as &quot;Not shared&quot;.
        </span>
      </div>
    </section>
  );
}
